import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditsContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { X, Loader2, CheckCircle, Coins, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BuyCreditsModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { packages, downloadCost, addCredits } = useCredits();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePurchase = async (pkg) => {
    if (!user) {
      toast.error('Please login to purchase credits');
      return;
    }

    setSelectedPackage(pkg.id);
    setLoading(true);

    try {
      // Load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(`${API}/credits/create-order`, {
        package_id: pkg.id,
        user_id: user.uid,
        user_email: user.email
      });

      const { order_id, amount, currency, key_id } = orderResponse.data;

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'CanvaForResume',
        description: `${pkg.credits} Credits - ${pkg.name}`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${API}/credits/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: user.uid
            });

            if (verifyResponse.data.success) {
              addCredits(verifyResponse.data.added);
              toast.success(`Successfully added ${verifyResponse.data.added} credits!`);
              onSuccess?.();
              onClose();
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.displayName || '',
          email: user.email || '',
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setSelectedPackage(null);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-slate-100 transition-colors"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>

        {/* Header */}
        <div className="p-6 pb-2 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Coins className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Buy Credits</h2>
          <p className="text-slate-600 mt-2">
            Each resume download costs <span className="font-semibold text-blue-600">{downloadCost} credits</span>
          </p>
        </div>

        {/* Packages */}
        <div className="p-6 grid gap-4">
          {packages.map((pkg) => (
            <Card 
              key={pkg.id}
              className={`relative cursor-pointer transition-all hover:shadow-md ${
                pkg.popular ? 'border-blue-500 border-2' : 'border-slate-200'
              }`}
              onClick={() => !loading && handlePurchase(pkg)}
            >
              {pkg.popular && (
                <Badge className="absolute -top-2 left-4 bg-blue-600">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Most Popular
                </Badge>
              )}
              
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    pkg.popular ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    <span className={`text-lg font-bold ${
                      pkg.popular ? 'text-blue-600' : 'text-slate-600'
                    }`}>
                      {pkg.credits}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{pkg.name}</p>
                    <p className="text-sm text-slate-500">
                      {Math.floor(pkg.credits / downloadCost)} downloads
                    </p>
                  </div>
                </div>
                
                <Button
                  className={`${
                    pkg.popular 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-slate-800 hover:bg-slate-900'
                  }`}
                  disabled={loading}
                >
                  {loading && selectedPackage === pkg.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    `₹${pkg.price}`
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 pt-2 text-center border-t border-slate-100">
          <p className="text-xs text-slate-500">
            Secure payments powered by Razorpay. UPI, Cards & Net Banking accepted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;